import React, { useState } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import SusuModal from '../common/SusuModal';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { selectVTOResult as selectVTOResultAction, updateVTOSession } from '../../redux/slices/vtoSlice';
import { setActiveItems } from '../../redux/slices/clothingItemsSlice';
import { setActivePersonImage } from '../../redux/slices/personImageSlice';
import './MyCloset.css';
import './MyClosetCanvas.css';
import './MyClosetItems.css';
import './MyClosetResize.css';
import './MyClosetHeader.css';
import './MyClosetVirtualTryOn.css';
import './MyClosetCards.css';
import './MyClosetUtils.css';
import ResizableLayout from '../resizable-layout/ResizableLayout';
import UnifiedCanvasControls from '../unified-canvas-controls/UnifiedCanvasControls';
import MyClosetCanvas from './MyClosetCanvas';
import ClothingItemsPanel from './ClothingItemsPanel';
import VTOBottomPanel from '../vto-bottom-panel/VTOBottomPanel';
import ManualControlPanel from '../manual-control-panel/ManualControlPanel';
import { ClosetPanelView } from '../closet-panel-view';
import { LookbookPanelView } from '../lookbook-panel-view';
import LookbookItemsPanel from '../lookbook-items-panel/LookbookItemsPanel';
import { extractShareToken } from '../../utils/shareLink';
import MyClosetModals from './MyClosetModals';
import EmailImportModal from '../email-import-modal/EmailImportModal';
import UrlImportModal from '../url-import-modal/UrlImportModal';
import ExtensionInstallModal from '../extension-install-modal/ExtensionInstallModal';
import SharedClosetBrowser from '../closet/SharedClosetBrowser';
import { useAddFromListingMutation } from '../../redux/apis/endpoints/closetApi';
import EarningsDashboard from '../earnings/EarningsDashboard';

import VirtualTryOnProcessingModal from '../virtual-try-on/VirtualTryOnProcessingModal';
import VirtualTryOnErrorModal from '../virtual-try-on/VirtualTryOnErrorModal';
import ErrorModal from '../common/ErrorModal';
import useMyClosetState from '../../hooks/useMyClosetState';
import useCanvasInteractions from '../../hooks/useCanvasInteractions';
import useDragDrop from '../../hooks/interactions/useDragDrop';
import useDirectCanvasPlacement from '../../hooks/interactions/useDirectCanvasPlacement';
import useVirtualTryOnInteractions from '../../hooks/interactions/useVirtualTryOnInteractions';
import useMyClosetActions from '../../hooks/useMyClosetActions';
import useResizableLayout from '../../hooks/useResizableLayout';
import { usePositioningShortcuts } from '../../hooks/useKeyboardShortcuts';
import usePersonImage from '../../hooks/usePersonImage';
import usePersonImages from '../../hooks/usePersonImages';
import useVTOAuthentication from '../../hooks/useVTOAuthentication';
import { useBackgrounds } from '../../hooks/useBackgrounds';
import { useTryOnSelection } from '../../hooks/useTryOnSelection';
import { useVTOIntegration } from '../../hooks/useVTOIntegration';
import { useVTOResults } from '../../hooks/useVTOResults';
import useVTOResultRestoration from '../../hooks/useVTOResultRestoration';
import { useClothingItems } from '../../hooks/useClothingItems';
import { useCredits } from '../../hooks/useCredits';
import useMyClosetModals from '../../hooks/useMyClosetModals';
import useMyClosetPanels from '../../hooks/useMyClosetPanels';
import usePositioningHandlers from '../../hooks/usePositioningHandlers';
import useBulkSelection from '../../hooks/useBulkSelection';
import useLookbookPanel from '../../hooks/useLookbookPanel';
import useMyClosetProps from '../../hooks/useMyClosetProps';

function MyCloset({ guest, scrollToClothingId, selectVtoId }) {
  // Initialize all state using custom hooks
  const dispatch = useDispatch();
  const state = useMyClosetState(guest);
  const isLoggedIn = !!localStorage.getItem('token');
  const navigate = useNavigate();

  // Modal state for new features
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [isUrlImportModalOpen, setIsUrlImportModalOpen] = useState(false);
  const [pendingShareUrl, setPendingShareUrl] = useState('');
  
  // Clipboard Interception State
  const [clipboardUrl, setClipboardUrl] = useState('');
  const [clipboardImage, setClipboardImage] = useState(null);
  const [isClipboardModalOpen, setIsClipboardModalOpen] = useState(false);

  // Track full drag-drop upload lifecycle (processing + API call)
  const [isDropUploading, setIsDropUploading] = useState(false);

  // Get clothing items to determine if closet has items (for header button visibility)
  const { clothingItems: actualClothingItems, uploadClothing } = useClothingItems();

  // Get credit balance for VTO operations
  const { balance: creditBalance, refetch: refetchCredits } = useCredits();

  // Initialize person image Redux state
  const personImageState = usePersonImage();
  const { hasPersonImage } = personImageState;

  // Get actual person image data from backend integration
  const personImagesData = usePersonImages();
  const { activePersonImage } = personImagesData;

  // Initialize background Redux state
  const backgroundState = useBackgrounds();

  // Initialize VTO authentication
  const { handleAuthReturn } = useVTOAuthentication();

  // Try-on selection for VTO
  const { isItemSelectedForTryOn, canSelectForTryOn } = useTryOnSelection();

  // Integrate existing VTO system with Redux
  useVTOIntegration(state.virtualTryOnResults, state.clothingItems);

  // Initialize VTO results hook for refetching
  const vtoResults = useVTOResults();

  // Initialize all modal states and handlers
  const modals = useMyClosetModals();

  // Initialize resizable layout
  const layout = useResizableLayout({
    defaultPanelPosition: 'right',
    defaultPanelWidth: 400,
    defaultPanelHeight: 250,
    defaultIsCollapsed: false,
    minPanelWidth: 280,
    maxPanelWidth: 600,
    minPanelHeight: 150,
    maxPanelHeight: 400,
    storageKey: 'myClosetLayoutState'
  });

  // Initialize panel states and handlers
  const panels = useMyClosetPanels({
    isVirtualMode: state.isVirtualMode,
    droppedItems: state.droppedItems,
    selectedItem: state.selectedItem,
    multiSelectedItems: state.multiSelectedItems,
    setSelectedItem: state.setSelectedItem,
    setMultiSelectedItems: state.setMultiSelectedItems,
    setIsVirtualMode: state.setIsVirtualMode,
    setIsPersonImageModalOpen: modals.setIsPersonImageModalOpen,
    setIsBackgroundModalOpen: modals.setIsBackgroundModalOpen,
    setIsLoginModalOpen: modals.setIsLoginModalOpen
  });

  // Initialize lookbook panel hook (manages panel view state and handlers)
  const lookbookPanel = useLookbookPanel({
    layout,
    actualClothingItems,
    personImagesData,
    personImageState
  });

  // Destructure for convenience
  const {
    showPanelView, setShowPanelView,
    panelViewType,
    closetPanelState, setClosetPanelState,
    lookbookPanelState, setLookbookPanelState,
    selectedClosetId,
    selectedClosetName,
    handleOpenLookbookPanel,
    handleLookbookEntrySelect,
    handleClosetSelect,
    handleLookbookSelect,
    handleCloseLookbookPanel,
    handleBackToClosets
  } = lookbookPanel;

  // Determine current panel view name for header
  const currentPanelViewName = panelViewType === 'lookbook'
    ? (lookbookPanelState?.currentLookbookName || 'My Lookbook')
    : selectedClosetName;

  // Initialize bulk selection handlers
  const bulkSelection = useBulkSelection({
    selectedItems: state.selectedItems,
    setSelectedItems: state.setSelectedItems,
    setIsBulkSelectMode: state.setIsBulkSelectMode,
    setIsBulkDeleteModalOpen: modals.setIsBulkDeleteModalOpen,
    setIsAddToClosetModalOpen: modals.setIsAddToClosetModalOpen,
    clothingItems: actualClothingItems
  });

  // Initialize actions using custom hook
  const actions = useMyClosetActions({
    ...state,
    personImage: activePersonImage?.image?.url || null,
    guest,
    showErrorModal: modals.showGeneralErrorModal,
    showVTOLoginModal: modals.showVTOLoginModal,
    navigate
  });

  // Initialize canvas interactions
  const canvasInteractions = useCanvasInteractions({
    droppedItems: state.droppedItems,
    setDroppedItems: state.setDroppedItems,
    setDroppedItemsRealtime: state.setDroppedItemsRealtime,
    commitDroppedItems: state.commitDroppedItems,
    selectedItem: state.selectedItem,
    setSelectedItem: state.setSelectedItem,
    multiSelectedItems: state.multiSelectedItems,
    setMultiSelectedItems: state.setMultiSelectedItems
  });

  // Initialize positioning handlers
  const positioning = usePositioningHandlers({
    showGrid: state.showGrid,
    setShowGrid: state.setShowGrid,
    showSnapGuides: state.showSnapGuides,
    setShowSnapGuides: state.setShowSnapGuides,
    setSnapLines: state.setSnapLines,
    gridSize: state.gridSize,
    setGridSize: state.setGridSize,
    actions: {
      ...actions,
      selectAll: canvasInteractions.selectAll,
      clearSelection: canvasInteractions.clearSelection,
      deleteSelected: canvasInteractions.deleteSelected,
      undoAction: state.undoAction,
      redoAction: state.redoAction
    }
  });

  // Enable keyboard shortcuts
  usePositioningShortcuts(positioning.keyboardHandlers, true);

  // Initialize drag and drop functionality
  const dragDrop = useDragDrop({
    dragOffset: state.dragOffset,
    setDragOffset: state.setDragOffset,
    droppedItems: state.droppedItems,
    setDroppedItems: state.setDroppedItems,
    setSelectedItem: state.setSelectedItem,
    placeholderRef: state.placeholderRef,
    placeholderWidth: state.placeholderWidth,
    onFileDropOnCanvas: React.useCallback(async (file) => {
      if (!isLoggedIn) {
        modals.setIsLoginModalOpen(true);
        return;
      }
      setIsDropUploading(true);
      try {
        // Clear existing VTO result / person image from canvas
        personImageState.revertToOriginal();
        dispatch(selectVTOResultAction(null));
        // Clear dropped clothing items from canvas
        state.setDroppedItems([], 'clear-for-drop');
        state.setSelectedItem(null);
        state.setMultiSelectedItems([]);

        // Process file first (HEIC conversion + compression) before uploading
        const { processFileForUpload } = await import('../../utils/imageCompression');
        const fileToUpload = await processFileForUpload(file, {
          maxWidth: 2048,
          maxHeight: 3072,
          targetSizeMB: 5,
          thresholdMB: 5,
          minQuality: 0.5,
          maxQuality: 0.9
        });

        // Upload processed file — uploadPersonImage already sets active + enables canvas integration
        await personImagesData.uploadPersonImage({
          file: fileToUpload,
          fileName: file.name || 'dropped-image'
        });
      } catch (err) {
        console.error('Failed to upload dropped person image:', err);
      } finally {
        setIsDropUploading(false);
      }
    }, [isLoggedIn, modals, personImageState, personImagesData, dispatch, state])
  });

  // Initialize direct canvas placement functionality
  const directPlacement = useDirectCanvasPlacement({
    droppedItems: state.droppedItems,
    setDroppedItems: state.setDroppedItems,
    setSelectedItem: state.setSelectedItem,
    placeholderWidth: state.placeholderWidth,
    placeholderHeight: state.placeholderHeight
  });

  // Sync canvas size with placeholder dimensions
  React.useEffect(() => {
    if (state.placeholderWidth && state.placeholderHeight) {
      personImageState.updateCanvasSize({
        width: state.placeholderWidth,
        height: state.placeholderHeight
      });
    }
  }, [state.placeholderWidth, state.placeholderHeight]);

  // Handle shared URLs from native app and clipboard URL interception
  React.useEffect(() => {
    // PostMessage listener for native app share extension
    const handleMessage = (event) => {
      try {
        let data = event.data;
        if (typeof data === 'string') {
          try {
            data = JSON.parse(data);
          } catch (e) {
            // Ignore parse errors, might not be JSON
          }
        }
        
        if (data && data.type === 'SHARED_URL_IMPORT' && data.url) {
          console.log('Received SHARED_URL_IMPORT:', data.url);
          setPendingShareUrl(data.url);
          setIsUrlImportModalOpen(true);
        } else if (data && data.type === 'SHARED_IMAGE_IMPORT' && data.dataUrl) {
          console.log('Received SHARED_IMAGE_IMPORT');
          
          fetch(data.dataUrl)
            .then(res => res.blob())
            .then(blob => {
              const file = new File([blob], "shared_image.jpg", { type: blob.type || 'image/jpeg' });
              return uploadClothing({ file, clothesName: 'Imported Item' });
            })
            .then(() => {
              console.log('Successfully uploaded shared image!');
            })
            .catch(err => {
              console.error('Failed to upload shared image:', err);
              modals.showGeneralErrorModal({
                title: 'Import Failed',
                message: 'Failed to save the shared image to your closet. Please try again.'
              });
            });
        }
      } catch (err) {
        console.error('Error handling postMessage:', err);
      }
    };

    window.addEventListener('message', handleMessage);

    // Clipboard listener for URL and Image interception
    const handleFocus = async () => {
      try {
        if (!navigator.clipboard) return;

        // Try to read image from clipboard first
        if (navigator.clipboard.read) {
          try {
            const clipboardItems = await navigator.clipboard.read();
            for (const item of clipboardItems) {
              const imageTypes = item.types.filter(type => type.startsWith('image/'));
              if (imageTypes.length > 0) {
                const blob = await item.getType(imageTypes[0]);
                const imgKey = `img_${blob.size}_${blob.type}`;
                const lastProcessed = sessionStorage.getItem('lastProcessedClipboardImage');
                if (lastProcessed !== imgKey) {
                  const file = new File([blob], "clipboard_image.png", { type: blob.type });
                  setTimeout(() => {
                    setClipboardImage(file);
                    setClipboardUrl('');
                    setIsClipboardModalOpen(true);
                  }, 500);
                  return; // Don't process text if image is found
                }
              }
            }
          } catch (err) {
            // read() might fail or not be allowed, fallback to text
          }
        }

        // Fallback to text (URL)
        if (navigator.clipboard.readText) {
          const text = await navigator.clipboard.readText();
          
          // A SUSU share link is handled by the share flow (ClipboardSharePrompt),
          // which offers to claim it. Importing it as a product URL would both
          // double-prompt and scrape our own page as if it were a garment.
          const isOwnShareLink = !!extractShareToken(text);

          if (text && !isOwnShareLink && (text.startsWith('http://') || text.startsWith('https://'))) {
            // Check if we already processed this URL in this session
            const lastProcessed = sessionStorage.getItem('lastProcessedClipboardUrl');
            if (lastProcessed !== text) {
              // Wait a brief moment to not disrupt immediate user action
              setTimeout(() => {
                setClipboardUrl(text);
                setClipboardImage(null);
                setIsClipboardModalOpen(true);
              }, 500);
            }
          }
        }
      } catch (err) {
        // Ignore clipboard access errors (user might not have granted permission)
        console.log('Clipboard access denied or failed', err);
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  // Handle VTO authentication return on component mount
  React.useEffect(() => {
    const checkVTOAuthReturn = async () => {
      const authResult = await handleAuthReturn();
      if (authResult.success && authResult.shouldOpenPersonImagePortal) {
        state.setIsVirtualMode(true);
      }
    };
    checkVTOAuthReturn();
  }, []);

  // Restore VTO result from backend on page refresh
  const { hasRestored } = useVTOResultRestoration(
    personImageState,
    personImagesData.activePersonImage?.id
  );

  React.useEffect(() => {
    if (hasRestored) {
      console.log('✅ VTO result restored from backend after refresh');
    }
  }, [hasRestored]);

  // Scroll to a specific clothing item when opened from sheyou VTO button
  const hasScrolledToItemRef = React.useRef(false);
  React.useEffect(() => {
    if (!scrollToClothingId || hasScrolledToItemRef.current) return;
    if (!actualClothingItems || actualClothingItems.length === 0) return;

    // Check if the target item exists in the loaded clothing items
    const targetItem = actualClothingItems.find(
      item => String(item.id) === String(scrollToClothingId)
    );
    if (!targetItem) return;

    hasScrolledToItemRef.current = true;

    // Ensure clothing panel is visible (switch from lookbook, expand if collapsed)
    if (lookbookPanel?.panelViewType === 'lookbook') {
      lookbookPanel.setPanelViewType('closet');
    }
    if (lookbookPanel?.showPanelView) {
      lookbookPanel.setShowPanelView(false);
    }
    if (layout.isCollapsed && layout.handleCollapseToggle) {
      layout.handleCollapseToggle(false);
    }

    // Scroll to the item after a short delay to allow panel to expand/render
    setTimeout(() => {
      const cardElement = document.querySelector(`[data-clothing-id="${scrollToClothingId}"]`);
      if (cardElement) {
        cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        cardElement.classList.add('highlight-pulse');
        setTimeout(() => cardElement.classList.remove('highlight-pulse'), 1500);
      }
    }, 500);
  }, [scrollToClothingId, actualClothingItems, lookbookPanel, layout]);

  // Auto-select VTO result from URL param (e.g. from virtual_space "View in Closet" button)
  const hasSelectedVtoRef = React.useRef(false);
  React.useEffect(() => {
    if (!selectVtoId || hasSelectedVtoRef.current) return;

    console.log('🎯 selectVto effect running:', {
      selectVtoId,
      isLoading: vtoResults.isLoading,
      resultsCount: vtoResults.vtoResults?.length,
      resultIds: vtoResults.vtoResults?.map(r => r.id),
      hasPersonImages: personImagesData.personImages?.length
    });

    if (vtoResults.isLoading) {
      console.log('🎯 selectVto: still loading, waiting...');
      return;
    }

    const targetId = parseInt(selectVtoId);
    const result = vtoResults.vtoResults?.find(r => r.id === targetId);

    if (!result) {
      console.log('🎯 selectVto: result not found in list, IDs available:', vtoResults.vtoResults?.map(r => ({ id: r.id, type: typeof r.id })));
      return;
    }

    console.log('🎯 selectVto: FOUND result!', { id: result.id, hasResultImage: !!(result.imageUrl || (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url)), personImageId: result.personImage?.id });
    hasSelectedVtoRef.current = true;

    // Enable VTO mode
    state.setIsVirtualMode(true);

    // Set active clothing items (replicate FitHistorySection.handleSelectHistory logic)
    let clothingItems = [];
    const metadataLayeredItems = result.generationMetadata?.layeredItems || result.metadata?.layeredItems;
    if (metadataLayeredItems && Array.isArray(metadataLayeredItems) && metadataLayeredItems.length > 0) {
      clothingItems = metadataLayeredItems
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map(metadataItem => {
          const fullItem = actualClothingItems.find(item => item.id === metadataItem.id);
          if (fullItem) {
            return { ...fullItem, imageUrl: fullItem.image?.url };
          }
          return {
            id: metadataItem.id,
            name: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
            clothesName: metadataItem.name || metadataItem.clothesName || 'Unknown Item',
            category: metadataItem.category || metadataItem.garmentType,
            image: null
          };
        })
        .filter(item => item != null);
    } else if (result.clothingItems) {
      clothingItems = Array.isArray(result.clothingItems) ? result.clothingItems : [result.clothingItems];
    } else if (result.closetClothes) {
      clothingItems = Array.isArray(result.closetClothes) ? result.closetClothes : [result.closetClothes];
    }
    clothingItems = clothingItems.filter(item => item != null).flat();
    dispatch(setActiveItems(clothingItems));

    // Set person image and VTO session
    const personId = result.personImage?.id || result.personImageId || result.person_image?.id;
    console.log('🎯 selectVto: personId =', personId);
    if (personId) {
      dispatch(setActivePersonImage({ personImageId: personId }));
      dispatch(updateVTOSession({
        personImageId: personId,
        vtoResultId: result.id,
        resultImageId: (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url) || result.imageUrl
      }));
    }

    // Display VTO result on canvas
    if (result.imageUrl || (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url)) {
      const shouldUseRemoved = result.useRemovedImage !== false && result.removedBgImage?.url;
      const vtoImageUrl = shouldUseRemoved ? (Array.isArray(result.removedBgImage) ? result.removedBgImage[0]?.url : result.removedBgImage?.url) : (result.imageUrl || (Array.isArray(result.resultImage) ? result.resultImage[0]?.url : result.resultImage?.url));

      const fullPersonImageData = personImagesData.personImages?.find(img => img.id === personId);
      const personImageUseRemovedFlag = fullPersonImageData?.useRemovedImage !== false;
      let originalPersonImageUrl;
      if (personImageUseRemovedFlag && fullPersonImageData?.removedBgImage?.url) {
        originalPersonImageUrl = fullPersonImageData.removedBgImage.url;
      } else if (fullPersonImageData?.image?.url) {
        originalPersonImageUrl = fullPersonImageData.image.url;
      } else {
        originalPersonImageUrl = result.personImageUrl || result.personImage?.url || result.person_image?.url;
      }

      console.log('🎯 selectVto: displaying on canvas', { vtoImageUrl, originalPersonImageUrl });

      if (originalPersonImageUrl) {
        personImageState.setVirtualTryOnResult({
          image: vtoImageUrl,
          originalImage: originalPersonImageUrl,
          metadata: {
            isVirtualTryOnResult: true,
            originalPersonImage: originalPersonImageUrl,
            garmentId: result.closetClothes?.id,
            garmentName: result.closetClothes?.clothesName,
            timestamp: result.created_at || result.createdAt,
            vtoResultId: result.id
          }
        });
        personImageState.setCanvasIntegrationMode(true);
      } else {
        console.log('🎯 selectVto: WARNING - no originalPersonImageUrl found!');
      }
    } else {
      console.log('🎯 selectVto: WARNING - no result image found!');
    }

    // Select in Redux (highlights the thumbnail in bottom panel)
    dispatch(selectVTOResultAction(result.id));

    // Expand the VTO bottom panel if collapsed
    if (layout.isCollapsed && layout.handleCollapseToggle) {
      layout.handleCollapseToggle(false);
    }
  }, [selectVtoId, vtoResults.isLoading, vtoResults.vtoResults, actualClothingItems, personImagesData.personImages]);

  // Track if we've already loaded the default image
  const hasLoadedDefaultRef = React.useRef(false);

  // Load default person image when entering closet
  React.useEffect(() => {
    const loadDefaultPersonImage = async () => {
      if (hasLoadedDefaultRef.current || hasPersonImage) return;
      if (!personImagesData.personImages || personImagesData.personImages.length === 0) return;

      const isLoggedIn = !!localStorage.getItem('token');

      if (!isLoggedIn) {
        const guestImages = JSON.parse(localStorage.getItem('guestPersonImages') || '[]');
        const defaultImage = guestImages.find(img => img.isDefault);
        if (defaultImage) {
          const imageUrl = defaultImage.removedBgImage?.url || defaultImage.image?.url;
          personImageState.setPersonImage(imageUrl);
          personImagesData.setCanvasIntegrationMode(true);
          hasLoadedDefaultRef.current = true;
        }
      } else {
        const defaultImage = personImagesData.personImages?.find(img => img.isDefault);
        if (defaultImage) {
          if (activePersonImage?.id === defaultImage.id) {
            hasLoadedDefaultRef.current = true;
            return;
          }
          try {
            await personImagesData.setActivePersonImage(defaultImage.id);
            personImagesData.setCanvasIntegrationMode(true);
            const imageUrl = defaultImage.removedBgImage?.url || defaultImage.image?.url;
            if (imageUrl) {
              personImageState.setPersonImage(imageUrl);
            }
            hasLoadedDefaultRef.current = true;
          } catch (error) {
            console.error('❌ Failed to load default person image:', error);
          }
        }
      }
    };
    loadDefaultPersonImage();
  }, [personImagesData.personImages, hasPersonImage]);

  // Handle VTO completion
  const handleVTOComplete = React.useCallback(async (result, item, action) => {
    if (action !== 'remove' && layout.setIsCollapsed) {
      layout.setIsCollapsed(true);
    }
    if (refetchCredits) refetchCredits();
    if (vtoResults.refetchVTOResults) {
      await vtoResults.refetchVTOResults();
      if (result?.backendResult?.id || result?.savedResultId) {
        const vtoResultId = result.backendResult?.id || result.savedResultId;
        if (vtoResults.selectVTOResult) {
          vtoResults.selectVTOResult(vtoResultId);
        }
      }
    }
  }, [vtoResults, refetchCredits, layout]);

  // Get active items for multi-item VTO
  const activeItems = useSelector((reduxState) => reduxState.clothingItems.activeItems);

  // Calculate the correct person image ID
  const correctPersonImageId = activePersonImage?.id || personImagesData.activePersonImageId || personImagesData.latestPersonImage?.id;

  // Initialize virtual try-on functionality
  const virtualTryOn = useVirtualTryOnInteractions({
    isVirtualMode: state.isVirtualMode,
    isAvailable: state.isAvailable,
    personImage: activePersonImage?.image?.url || null,
    toggleMode: state.toggleMode,
    tryOnGarment: state.tryOnGarment,
    clearTryOnError: state.clearTryOnError,
    virtualTryOnResults: state.virtualTryOnResults,
    setVirtualTryOnResults: state.setVirtualTryOnResults,
    droppedItems: state.droppedItems,
    setDroppedItems: state.setDroppedItems,
    placeholderWidth: state.placeholderWidth,
    placeholderHeight: state.placeholderHeight,
    showProcessingModal: modals.showProcessingModal,
    hideProcessingModal: modals.hideProcessingModal,
    showErrorModal: modals.showErrorModal,
    personImageState,
    activePersonImageId: correctPersonImageId,
    activePersonImage,
    onVTOComplete: handleVTOComplete,
    activeItems,
    dispatch
  });

  // Prepare memoized props for large child components
  const handleUseVirtualTryOn = React.useCallback(() => {
    // 1. Check if person image is uploaded
    if (!hasPersonImage && !activePersonImage) {
      modals.setIsPersonImageModalOpen(true);
      return;
    }
    // 2. We have a person image and dropped items.
    virtualTryOn.generateVTOFromCanvasItems(state.droppedItems);
  }, [hasPersonImage, activePersonImage, modals, virtualTryOn, state.droppedItems]);

  const componentProps = useMyClosetProps({
    state,
    layout,
    modals,
    panels,
    actions,
    positioning,
    virtualTryOn,
    dragDrop,
    canvasInteractions,
    bulkSelection,
    backgroundState,
    personImageState,
    activePersonImage,
    hasPersonImage,
    actualClothingItems,
    creditBalance,
    isItemSelectedForTryOn,
    canSelectForTryOn,
    selectedClosetId,
    guest,
    isLoggedIn,
    lookbookPanel,
    onOpenEmailModal: () => setIsEmailModalOpen(true),
    onOpenExtensionModal: () => setIsExtensionModalOpen(true),
    onOpenUrlImportModal: () => setIsUrlImportModalOpen(true),
    isDropUploading
  });

  // Inject the new onUseVirtualTryOn prop into manualControlPanelProps
  componentProps.manualControlPanelProps = {
    ...componentProps.manualControlPanelProps,
    onUseVirtualTryOn: handleUseVirtualTryOn
  };

  // Create unified card click handler
  const handleUnifiedCardClick = React.useCallback((item, e) => {
    if (state.isVirtualMode && activePersonImage?.image?.url) {
      if (!personImageState.displayProps.isCanvasIntegrated) {
        personImageState.setCanvasIntegrationMode(true);
      }
      return virtualTryOn.handleCardClick(item, e);
    } else if (state.isVirtualMode && !activePersonImage?.image?.url) {
      return virtualTryOn.handleCardClick(item, e);
    } else {
      return directPlacement.handleCardClick(item, e);
    }
  }, [state.isVirtualMode, activePersonImage?.image?.url, virtualTryOn.handleCardClick, directPlacement.handleCardClick, personImageState.displayProps]);

  const [addFromListing] = useAddFromListingMutation();

  /**
   * Clicking a community card wears the piece, exactly like clicking one in the
   * closet strip.
   *
   * The two kinds of card need different preparation. A shared look is already
   * a closet-clothes row — someone else's, but the try-on only needs its id, and
   * attribution pays whoever owns it. A designer piece is a `products` row with
   * no closet row anywhere, and the whole try-on pipeline (layering, removal,
   * cache keys) is keyed on closet ids, so it gets added to the viewer's closet
   * first. The backend stamps the designer as originalUploader, so wearing it
   * pays them and not the person who just added it.
   */
  const handleCommunityItemClick = React.useCallback(async (item, e) => {
    if (!item) return;

    if (item.entityType === 'product') {
      try {
        const added = await addFromListing({
          listingId: item.entityId,
          closetId: selectedClosetId || undefined,
          clothesName: item.clothesName
        }).unwrap();
        return handleUnifiedCardClick({ ...added, image: added.image || item.image }, e);
      } catch (err) {
        console.error('[Community] Could not add designer piece to closet:', err);
        return;
      }
    }

    // Shared look: strip the composite feed id back to the closet row id the
    // try-on pipeline expects.
    return handleUnifiedCardClick({ ...item, id: item.entityId ?? item.id }, e);
  }, [addFromListing, handleUnifiedCardClick, selectedClosetId]);

  return (
    <div className={`my-closet ${state.isBulkSelectMode ? 'bulk-select-mode' : ''} ${panels.isControlsCollapsed ? 'controls-collapsed' : ''}`}>
      <ResizableLayout
        {...componentProps.resizableLayoutProps}
        panelViewContent={
          panelViewType === 'lookbook' ? (
            <LookbookPanelView
              panelPosition={layout.panelPosition}
              onStateChange={setLookbookPanelState}
              onLookbookSelect={handleLookbookSelect}
              onEntrySelect={handleLookbookEntrySelect}
              onClose={handleCloseLookbookPanel}
              viewMode="list"
            />
          ) : panelViewType === 'explore' ? (
            <SharedClosetBrowser
              onItemClick={handleCommunityItemClick}
              onBack={() => lookbookPanel.setPanelViewType('closet')}
            />
          ) : (
            <ClosetPanelView
              panelPosition={layout.panelPosition}
              onStateChange={setClosetPanelState}
              onClosetSelect={handleClosetSelect}
            />
          )
        }
        leftPanel={
          <div className="main-canvas-area">
            <UnifiedCanvasControls {...componentProps.unifiedCanvasControlsProps} />
            <MyClosetCanvas {...componentProps.myClosetCanvasProps} />
          </div>
        }
        rightPanel={
          panelViewType === 'lookbook' ? (
            <LookbookItemsPanel
              {...componentProps.lookbookItemsPanelProps}
            />
          ) : panelViewType === 'explore' ? (
            <SharedClosetBrowser
              onItemClick={handleCommunityItemClick}
              onBack={() => lookbookPanel.setPanelViewType('closet')}
            />
          ) : (
            <ClothingItemsPanel
              {...componentProps.clothingItemsPanelBaseProps}
              onCardClick={handleUnifiedCardClick}
              panelWidth={layout.panelWidth}
              panelPosition={layout.panelPosition}
              onBulkEditClick={componentProps.handleBulkEditClick}
            />
          )
        }
        bottomPanel={
          panelViewType === 'lookbook' ? (
            <LookbookItemsPanel
              {...componentProps.lookbookItemsPanelProps}
            />
          ) : panelViewType === 'explore' ? (
            <SharedClosetBrowser
              onItemClick={handleCommunityItemClick}
              onBack={() => lookbookPanel.setPanelViewType('closet')}
            />
          ) : (
            <ClothingItemsPanel
              {...componentProps.clothingItemsPanelBaseProps}
              onCardClick={handleUnifiedCardClick}
              panelWidth={layout.panelWidth}
              panelHeight={layout.panelHeight}
              panelPosition={layout.panelPosition}
              onBulkEditClick={componentProps.handleBulkEditClick}
            />
          )
        }
      />

      {/* Virtual Try-On Processing Modal */}
      <VirtualTryOnProcessingModal {...componentProps.processingModalProps} />

      {/* Virtual Try-On Error Modal */}
      <VirtualTryOnErrorModal {...componentProps.errorModalProps} />

      {/* General Error Modal */}
      <ErrorModal {...componentProps.generalErrorModalProps} />


      {/* VTO Bottom Panel */}
      <VTOBottomPanel
        {...componentProps.vtoBottomPanelProps}
        onOpenLookbookPanel={handleOpenLookbookPanel}
      />

      {/* Manual Control Panel */}
      {/* <ManualControlPanel {...componentProps.manualControlPanelProps} /> */}

      {/* All Modals */}
      <MyClosetModals
        {...componentProps.myClosetModalsProps}
        onAddToClosetSuccess={() => {
          state.setIsBulkSelectMode(false);
          state.setSelectedItems([]);
          setShowPanelView(true);
        }}
      />

      {/* Email Import Modal */}
      <EmailImportModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
      />

      {/* Extension Install Modal */}
      <ExtensionInstallModal
        open={isExtensionModalOpen}
        onClose={() => setIsExtensionModalOpen(false)}
      />

      {/* URL Import Modal */}
      <UrlImportModal
        open={isUrlImportModalOpen}
        onClose={() => setIsUrlImportModalOpen(false)}
        initialUrl={pendingShareUrl}
      />

      {/* Clipboard Modal */}
      <SusuModal
        open={isClipboardModalOpen}
        onClose={() => {
          setIsClipboardModalOpen(false);
          setClipboardImage(null);
              setClipboardUrl('');
        }}
        size="tiny"
      >
        <SusuModal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="clipboard outline" style={{ margin: 0 }} />
            <span>{clipboardImage ? 'Image Detected' : 'Link Detected'}</span>
          </div>
        </SusuModal.Header>
        <SusuModal.Content>
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <Icon
              name={clipboardImage ? 'image' : 'linkify'}
              size="huge"
              color="grey"
              style={{ marginBottom: '1rem' }}
            />
            <p style={{ fontSize: '1.1rem', color: '#666' }}>
              We found {clipboardImage ? 'an image' : 'a link'} in your clipboard.
              Would you like to import it to your closet?
            </p>
          </div>
        </SusuModal.Content>
        <SusuModal.Actions>
          <Button
            basic
            onClick={() => {
              setIsClipboardModalOpen(false);
              setClipboardImage(null);
                  setClipboardUrl('');
            }}
          >
            Cancel
          </Button>
          <Button
            color="teal"
            onClick={() => {
              if (clipboardImage) {
                sessionStorage.setItem('lastProcessedClipboardImage', `img_${clipboardImage.size}_${clipboardImage.type}`);
                setIsClipboardModalOpen(false);
                componentProps.onImportClipboardImage(clipboardImage);
              } else if (clipboardUrl) {
                sessionStorage.setItem('lastProcessedClipboardUrl', clipboardUrl);
                setIsClipboardModalOpen(false);
                componentProps.onImportClipboardUrl(clipboardUrl);
                setIsUrlImportModalOpen(true);
              }
            }}
          >
            <Icon name="download" /> {clipboardImage ? 'Import Image' : 'Import Link'}
          </Button>
        </SusuModal.Actions>
      </SusuModal>

      {/* Earnings Dashboard Modal */}
      <SusuModal
        open={modals.isEarningsModalOpen}
        onClose={() => modals.setIsEarningsModalOpen(false)}
        size="small"
        closeIcon
      >
        <SusuModal.Header>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Icon name="wallet" style={{ margin: 0 }} />
            <span>My Wallet</span>
          </div>
        </SusuModal.Header>
        <SusuModal.Content scrolling style={{ backgroundColor: '#f9f9f9', padding: '1.5rem' }}>
          <EarningsDashboard />
        </SusuModal.Content>
      </SusuModal>
    </div>
  );
}

export default MyCloset;
